import { useEffect, useMemo, useRef, useState } from 'react';
import { Text } from '@react-three/drei';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import type { LogEntry } from '../types';
import { severityColor, severityHex } from '../utils/colors';

interface LogParticleStreamProps {
  logs: LogEntry[];
  servicePositions: Map<string, THREE.Vector3Tuple>;
  onSelectLog: (log: LogEntry) => void;
}

const MAX_RENDERED_POINTS = 1800;
const MAX_PICKABLE = 180;

const playErrorBeep = (position: THREE.Vector3Tuple) => {
  const AudioContextImpl =
    window.AudioContext ||
    (window as typeof window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!AudioContextImpl) {
    return;
  }

  const audioContext = new AudioContextImpl();
  const oscillator = audioContext.createOscillator();
  const gainNode = audioContext.createGain();
  const panner = audioContext.createPanner();

  panner.panningModel = 'HRTF';
  panner.distanceModel = 'inverse';
  panner.refDistance = 1;
  panner.maxDistance = 30;
  panner.rolloffFactor = 1.4;
  panner.positionX.value = position[0];
  panner.positionY.value = position[1];
  panner.positionZ.value = position[2];

  oscillator.type = 'triangle';
  oscillator.frequency.value = 660;
  gainNode.gain.setValueAtTime(0.001, audioContext.currentTime);
  gainNode.gain.exponentialRampToValueAtTime(0.09, audioContext.currentTime + 0.02);
  gainNode.gain.exponentialRampToValueAtTime(0.001, audioContext.currentTime + 0.16);

  oscillator.connect(gainNode);
  gainNode.connect(panner);
  panner.connect(audioContext.destination);

  oscillator.start();
  oscillator.stop(audioContext.currentTime + 0.17);
  oscillator.onended = () => {
    void audioContext.close();
  };
};

const getParticlePosition = (
  index: number,
  log: LogEntry,
  servicePositions: Map<string, THREE.Vector3Tuple>,
  nowMs: number
): THREE.Vector3Tuple => {
  const serviceStart = servicePositions.get(log.serviceName) ?? [0, 0, 0];
  const age = Math.max(0, (nowMs - log.timestamp) / 1000);
  const progress = Math.min(age / 20, 1);
  const laneOffset = ((index % 9) - 4) * 0.1;

  const x = THREE.MathUtils.lerp(serviceStart[0], laneOffset, progress);
  const y = THREE.MathUtils.lerp(serviceStart[1], (index % 7) * 0.08 - 0.24, progress);
  const z = THREE.MathUtils.lerp(serviceStart[2], 15 - progress * 30, progress);
  return [x, y, z];
};

export default function LogParticleStream({ logs, servicePositions, onSelectLog }: LogParticleStreamProps) {
  const pointsRef = useRef<THREE.Points>(null);
  const pickableRef = useRef<THREE.InstancedMesh>(null);
  const sourceLogsRef = useRef<LogEntry[]>([]);
  const pointPositions = useMemo(() => new Float32Array(MAX_RENDERED_POINTS * 3), []);
  const pointColors = useMemo(() => new Float32Array(MAX_RENDERED_POINTS * 3), []);
  const pickableLogsRef = useRef<LogEntry[]>([]);
  const dummy = useMemo(() => new THREE.Object3D(), []);
  const [now, setNow] = useState(Date.now());
  const latestErrorId = useRef<string | null>(null);

  useEffect(() => {
    sourceLogsRef.current = logs.slice(-MAX_RENDERED_POINTS);
  }, [logs]);

  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 120);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    const points = pointsRef.current;
    if (!points) {
      return;
    }

    const geometry = points.geometry;
    geometry.setAttribute('position', new THREE.BufferAttribute(pointPositions, 3));
    geometry.setAttribute('color', new THREE.BufferAttribute(pointColors, 3));
  }, [pointColors, pointPositions]);

  useEffect(() => {
    const newestError = [...logs].reverse().find((log) => log.severity === 'error');
    if (!newestError || newestError.id === latestErrorId.current) {
      return;
    }

    latestErrorId.current = newestError.id;
    const position = servicePositions.get(newestError.serviceName) ?? [0, 0, 0];
    playErrorBeep(position);
  }, [logs, servicePositions]);

  useFrame(() => {
    const points = pointsRef.current;
    if (!points) {
      return;
    }

    const renderedLogs = sourceLogsRef.current;
    const nowMs = Date.now();
    const count = renderedLogs.length;

    for (let i = 0; i < MAX_RENDERED_POINTS; i += 1) {
      const cursor = i * 3;
      if (i >= count) {
        pointPositions[cursor] = 0;
        pointPositions[cursor + 1] = -999;
        pointPositions[cursor + 2] = 0;
        pointColors[cursor] = 0;
        pointColors[cursor + 1] = 0;
        pointColors[cursor + 2] = 0;
        continue;
      }

      const log = renderedLogs[i];
      const [x, y, z] = getParticlePosition(i, log, servicePositions, nowMs);
      pointPositions[cursor] = x;
      pointPositions[cursor + 1] = y;
      pointPositions[cursor + 2] = z;

      const color = severityColor(log.severity);
      pointColors[cursor] = color.r;
      pointColors[cursor + 1] = color.g;
      pointColors[cursor + 2] = color.b;
    }

    const geometry = points.geometry;
    const positionAttribute = geometry.getAttribute('position') as THREE.BufferAttribute | undefined;
    const colorAttribute = geometry.getAttribute('color') as THREE.BufferAttribute | undefined;
    if (positionAttribute) {
      positionAttribute.needsUpdate = true;
    }
    if (colorAttribute) {
      colorAttribute.needsUpdate = true;
    }
  });

  useFrame(() => {
    const instanced = pickableRef.current;
    if (!instanced) {
      return;
    }

    const source = sourceLogsRef.current;
    const pickableLogs = source.slice(-MAX_PICKABLE);
    pickableLogsRef.current = pickableLogs;

    for (let i = 0; i < MAX_PICKABLE; i += 1) {
      if (i >= pickableLogs.length) {
        dummy.position.set(0, -999, 0);
        dummy.scale.setScalar(0.001);
      } else {
        const log = pickableLogs[i];
        const [x, y, z] = getParticlePosition(i, log, servicePositions, Date.now());
        dummy.position.set(x, y, z);
        dummy.scale.setScalar(log.severity === 'error' ? 0.26 : 0.15);
      }

      dummy.updateMatrix();
      instanced.setMatrixAt(i, dummy.matrix);
      if (i < pickableLogs.length) {
        instanced.setColorAt(i, severityColor(pickableLogs[i].severity));
      }
    }

    instanced.instanceMatrix.needsUpdate = true;
    if (instanced.instanceColor) {
      instanced.instanceColor.needsUpdate = true;
    }
  });

  const labels = useMemo(
    () =>
      logs
        .filter((log) => now - log.timestamp < 8_000)
        .slice(-28)
        .map((log, index) => {
          const [x, y, z] = getParticlePosition(index, log, servicePositions, now);
          const ageMs = Math.max(0, now - log.timestamp);
          const opacity = Math.max(0, 1 - ageMs / 8_000);
          return {
            id: log.id,
            log,
            position: [x + 0.22, y + 0.24, z] as THREE.Vector3Tuple,
            opacity
          };
        }),
    [logs, now, servicePositions]
  );

  const activeExplosions = useMemo(
    () =>
      logs
        .filter((log) => log.severity === 'error' && now - log.timestamp < 1200)
        .slice(-16)
        .map((log) => {
          const [x, y, z] = servicePositions.get(log.serviceName) ?? [0, 0, 0];
          const age = Math.max(0, now - log.timestamp) / 1200;
          return {
            id: log.id,
            position: [x, y, z] as THREE.Vector3Tuple,
            radius: 0.45 + age * 1.4,
            opacity: Math.max(0, 0.6 - age * 0.6)
          };
        }),
    [logs, now, servicePositions]
  );

  return (
    <group>
      <points ref={pointsRef} frustumCulled={false}>
        <bufferGeometry />
        <pointsMaterial
          size={0.085}
          vertexColors
          transparent
          opacity={0.88}
          sizeAttenuation
          blending={THREE.AdditiveBlending}
          depthWrite={false}
        />
      </points>

      <instancedMesh
        ref={pickableRef}
        args={[undefined, undefined, MAX_PICKABLE]}
        onClick={(event) => {
          if (typeof event.instanceId !== 'number') {
            return;
          }

          const selected = pickableLogsRef.current[event.instanceId];
          if (selected) {
            onSelectLog(selected);
          }
        }}
      >
        <sphereGeometry args={[0.1, 10, 10]} />
        <meshBasicMaterial transparent opacity={0.18} toneMapped={false} />
      </instancedMesh>

      {labels.map((label) => (
        <group key={label.id} position={label.position} onClick={() => onSelectLog(label.log)}>
          <Text
            maxWidth={4}
            fontSize={0.17}
            color={severityHex(label.log.severity)}
            anchorX="left"
            anchorY="middle"
            outlineWidth={0.012}
            outlineColor="#051024"
            fillOpacity={label.opacity}
            outlineOpacity={label.opacity}
          >
            {`${label.log.serviceName}: ${label.log.body.slice(0, 54)}`}
          </Text>
        </group>
      ))}

      {activeExplosions.map((burst) => (
        <mesh key={burst.id} position={burst.position}>
          <sphereGeometry args={[burst.radius, 18, 18]} />
          <meshBasicMaterial color="#ff5f86" transparent opacity={burst.opacity} wireframe toneMapped={false} />
        </mesh>
      ))}
    </group>
  );
}
